export type QueryValue = string | number | boolean | null | undefined | Array<string | number | boolean>;
export type QueryObject = Record<string, QueryValue>;

export function parseQuery(search: string): Record<string, string | string[]> {
  const out: Record<string, string | string[]> = {};
  const cleaned = search.startsWith('?') ? search.slice(1) : search;
  if (!cleaned) return out;
  for (const pair of cleaned.split('&')) {
    if (!pair) continue;
    const eq = pair.indexOf('=');
    const rawKey = eq === -1 ? pair : pair.slice(0, eq);
    const rawValue = eq === -1 ? '' : pair.slice(eq + 1);
    const key = decodeURIComponent(rawKey.replace(/\+/g, ' '));
    const value = decodeURIComponent(rawValue.replace(/\+/g, ' '));
    if (key in out) {
      const existing = out[key];
      out[key] = Array.isArray(existing) ? [...existing, value] : [existing as string, value];
    } else {
      out[key] = value;
    }
  }
  return out;
}

export function stringifyQuery(obj: QueryObject, opts: { sort?: boolean; omitEmpty?: boolean } = {}): string {
  const { sort = true, omitEmpty = true } = opts;
  const keys = Object.keys(obj);
  if (sort) keys.sort();
  const parts: string[] = [];
  for (const key of keys) {
    const value = obj[key];
    if (omitEmpty && (value === undefined || value === null || value === '')) continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(item))}`);
      }
    } else {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
    }
  }
  return parts.join('&');
}

export function mergeQuery(url: string, patch: QueryObject): string {
  const [base, hash] = url.split('#');
  const [path, search = ''] = (base ?? '').split('?');
  const current = parseQuery(search);
  const merged: QueryObject = { ...current };
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined || v === null) delete merged[k];
    else merged[k] = v;
  }
  const qs = stringifyQuery(merged);
  return (path ?? '') + (qs ? '?' + qs : '') + (hash ? '#' + hash : '');
}

export function removeQueryParam(url: string, key: string): string {
  return mergeQuery(url, { [key]: undefined });
}

export function appendQuery(url: string, obj: QueryObject): string {
  const [base, hash] = url.split('#');
  const [path, search = ''] = (base ?? '').split('?');
  const qs = stringifyQuery(obj);
  const combined = [search, qs].filter(Boolean).join('&');
  return (path ?? '') + (combined ? '?' + combined : '') + (hash ? '#' + hash : '');
}
